import type { Giveaway } from '@/models/giveaway'

/** Words shorter than this are too vague to match genres and platforms. */
const MIN_TAG_WORD = 2

/**
 * How well a giveaway matches a search, or 0 for no match. Name matches (the
 * giveaway's or its game's) always outrank genre and platform matches, which
 * outrank a match in the description alone.
 */
export function giveawaySearchScore(giveaway: Giveaway, query: string): number {
  const needle = query.trim().toLowerCase()
  if (!needle) return 1

  let score = 0
  for (const name of [giveaway.game_info?.name, giveaway.name]) {
    const value = name?.toLowerCase()
    if (!value) continue
    if (value === needle) score = Math.max(score, 1000)
    else if (value.startsWith(needle)) score = Math.max(score, 800)
    else if (value.includes(needle)) score = Math.max(score, 600)
  }

  // Each word of the query found in a genre or platform adds a little, so
  // "rpg switch" ranks games with both above games with one.
  const tags = (giveaway.game_info?.tags ?? []).map((tag) => tag.toLowerCase())
  for (const word of needle.split(/\s+/)) {
    if (word.length >= MIN_TAG_WORD && tags.some((tag) => tag.includes(word))) score += 10
  }

  if (score === 0 && giveaway.description?.toLowerCase().includes(needle)) score = 1
  return score
}

/** Giveaways matching the query, best first; ties keep their feed order. */
export function searchGiveaways(giveaways: Giveaway[], query: string): Giveaway[] {
  if (!query.trim()) return giveaways
  return giveaways
    .map((giveaway, index) => ({ giveaway, index, score: giveawaySearchScore(giveaway, query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.giveaway)
}

export interface GiveawayFilters {
  genre: string | null
  platform: string | null
}

export function matchesFilters(giveaway: Giveaway, filters: GiveawayFilters): boolean {
  const game = giveaway.game_info
  if (filters.genre && !game?.genres?.includes(filters.genre)) return false
  if (filters.platform && !game?.platforms?.includes(filters.platform)) return false
  return true
}

export interface FilterOption {
  value: string
  count: number
}

/**
 * The genres or platforms among these giveaways, most common first, with how
 * many giveaways each would leave. The selected value is always listed, even
 * when nothing matches it any more, so the control can still show it.
 */
export function filterOptions(
  giveaways: Giveaway[],
  field: 'genres' | 'platforms',
  selected: string | null,
): FilterOption[] {
  const counts = new Map<string, number>()
  for (const giveaway of giveaways) {
    for (const value of new Set(giveaway.game_info?.[field] ?? [])) {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  if (selected && !counts.has(selected)) counts.set(selected, 0)
  return [...counts]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
}

export type GiveawaySort = 'relevance' | 'newest' | 'ending' | 'entries' | 'value'

export const SORT_LABELS: Record<GiveawaySort, string> = {
  relevance: 'Best match',
  newest: 'Newest',
  ending: 'Ending soon',
  entries: 'Most entries',
  value: 'Highest value',
}

export function isGiveawaySort(value: string | null): value is GiveawaySort {
  return value !== null && value in SORT_LABELS
}

const time = (date: Date | null | undefined) => date?.getTime() ?? 0

/**
 * Orders giveaways for the search page. "relevance" keeps the order given,
 * which is searchGiveaways' best-first ranking. The sort is stable, so ties
 * keep that order too.
 */
export function sortGiveaways(giveaways: Giveaway[], sort: GiveawaySort): Giveaway[] {
  if (sort === 'relevance') return giveaways
  const sorted = [...giveaways]
  switch (sort) {
    case 'newest':
      return sorted.sort(
        (a, b) => time(b.created_at ?? b.scheduled_at) - time(a.created_at ?? a.scheduled_at),
      )
    case 'ending':
      // Live giveaways soonest first; ended ones after, most recent first.
      return sorted.sort((a, b) => {
        if (a.has_ended !== b.has_ended) return a.has_ended ? 1 : -1
        return a.has_ended
          ? time(b.finishes_at) - time(a.finishes_at)
          : time(a.finishes_at) - time(b.finishes_at)
      })
    case 'entries':
      return sorted.sort((a, b) => b.display_total_entries - a.display_total_entries)
    case 'value':
      // Games without a recorded price (null or 0) go last, not first.
      return sorted.sort((a, b) => (b.game_info?.price_usd || -1) - (a.game_info?.price_usd || -1))
  }
}
